import * as React from 'react';

import type { Row, Table as TanstackTable } from '@tanstack/react-table';

import { TableCell, TableRow } from '@/components/ui/table';

import { DataRow, getRowStateKey, type RowMemoKeys } from './data-row';
import {
  type RowAnimationPhase,
  useRowEnterLeaveAnimation,
} from './hooks/use-row-enter-leave-animation';
import type { TableBodyBaseProps } from './types';

export interface TableBodyContentProps<TData> extends TableBodyBaseProps<TData> {
  /**
   * Rows for which this returns true play a grow-in animation when they mount and a shrink-out
   * when they leave, so rows below slide instead of jumping. Meant for sub-rows revealed by
   * expanding a parent.
   */
  shouldAnimateRowEnter?: (row: TData) => boolean;
  /**
   * Called with each rendered row's id and its DOM element (or null on unmount). Lets callers
   * attach per-row observers (e.g. nested infinite scroll).
   */
  registerRowRef?: (rowId: string, el: HTMLTableRowElement | null) => void;
  /**
   * Per-row override: when it returns content, the row renders as one full-width cell holding it
   * instead of per-column cells. Use for inline spinner / status rows.
   */
  renderFullWidthRow?: (row: TData) => React.ReactNode | undefined;
}

/** Flat (non-grouped) body: memoized rows, optional enter/leave animation, full-width rows. */
export function TableBodyContent<TData>({
  table,
  isScrolled,
  onRowClick,
  getRowHref,
  getRowClassName,
  shouldAnimateRowEnter,
  variant,
  tableId,
  filledColumnWidths,
  registerRowRef,
  renderFullWidthRow,
  lastRowChildren,
}: TableBodyContentProps<TData>) {
  const renderList = useRowEnterLeaveAnimation({
    rows: table.getRowModel().rows,
    shouldAnimate: shouldAnimateRowEnter,
  });
  const memoKeys = useRowMemoKeys(table);

  const renderRow = (row: Row<TData>, phase: RowAnimationPhase) => {
    const fullWidthContent = phase === 'leave' ? undefined : renderFullWidthRow?.(row.original);
    if (fullWidthContent !== undefined) {
      return (
        <FullWidthRow
          key={row.id}
          row={row}
          columnCount={table.getVisibleLeafColumns().length}
          className={getRowClassName?.(row.original)}
          registerRowRef={registerRowRef}
        >
          {fullWidthContent}
        </FullWidthRow>
      );
    }

    return (
      <DataRow<TData>
        // Same key in every phase: a leaving row keeps its live key so React re-renders the
        // existing cell instances with `phase="leave"` instead of remounting them.
        key={row.id}
        row={row}
        phase={phase}
        hasAnimationWrapper={shouldAnimateRowEnter?.(row.original) ?? false}
        rowClassName={getRowClassName?.(row.original)}
        rowStateKey={getRowStateKey(row)}
        {...memoKeys}
        filledColumnWidths={filledColumnWidths}
        isScrolled={isScrolled}
        variant={variant}
        tableId={tableId}
        onRowClick={onRowClick}
        getRowHref={getRowHref}
        registerRowRef={registerRowRef}
      />
    );
  };

  return (
    <>
      {renderList.map(({ row, phase }) => renderRow(row, phase))}
      {lastRowChildren}
    </>
  );
}

/** Table-wide state every memoized row compares against (see `RowMemoKeys`). */
function useRowMemoKeys<TData>(
  table: TanstackTable<TData>
): Omit<RowMemoKeys<TData>, 'rowStateKey'> {
  const { columnSizing, columnPinning } = table.getState();
  return {
    columnDefs: table.options.columns,
    columnArrangementKey: table
      .getVisibleLeafColumns()
      .map(column => column.id)
      .join(','),
    columnSizing,
    columnPinning,
  };
}

interface FullWidthRowProps<TData> {
  row: Row<TData>;
  columnCount: number;
  className?: string;
  registerRowRef?: (rowId: string, el: HTMLTableRowElement | null) => void;
  children: React.ReactNode;
}

/**
 * One cell spanning all columns. Escapes per-column width/clipping so content can center across
 * the table. Keeps the cell's vertical padding (matches a normal row's height) but drops the
 * horizontal padding so sticky full-width content can span edge to edge.
 */
function FullWidthRow<TData>({
  row,
  columnCount,
  className,
  registerRowRef,
  children,
}: FullWidthRowProps<TData>) {
  return (
    <TableRow
      ref={registerRowRef ? el => registerRowRef(row.id, el) : undefined}
      className={className}
    >
      <TableCell colSpan={columnCount} className="px-0">
        {children}
      </TableCell>
    </TableRow>
  );
}
